import React from "react";
import * as XLSX from "xlsx";
import ImportadorExcelGenerico from "../../../../../Components/ImportadorExcelGenerico";
import { encodeId, decodeId } from "../../../../../Components/idEncoder";

const columnasPreview = [
  { title: "Nombre", dataIndex: "nombre", key: "nombre", width: 240 },
  {
    title: "# Insumos",
    key: "numInsumos",
    width: 110,
    render: (_, record) => (record.insumos || []).length,
  },
  {
    title: "Insumos (vista previa)",
    key: "insumos",
    render: (_, record) => {
      const lista = record.insumos || [];
      const head = lista
        .slice(0, 5)
        .map(
          (i) =>
            `${i.codigoInsumo}${i.requerido ? " *" : ""} x${i.cantidad}`,
        )
        .join(", ");
      const cola = lista.length > 5 ? ` … (+${lista.length - 5})` : "";
      return head + cola;
    },
  },
];

const construirPlantilla = (catalogos) => {
  const wb = XLSX.utils.book_new();

  const ejemploCodigo =
    (catalogos.insumos && catalogos.insumos[0]?.codigo) ?? 1;

  const hojaListas = XLSX.utils.json_to_sheet(
    [{ ID: "", Nombre: "Lista de Útiles 1° Primaria" }],
    { header: ["ID", "Nombre"] },
  );
  hojaListas["!cols"] = [{ wch: 20 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, hojaListas, "Listas");

  const hojaInsumos = XLSX.utils.json_to_sheet(
    [
      {
        "Nombre Lista": "Lista de Útiles 1° Primaria",
        "Código Insumo": ejemploCodigo,
        Cantidad: 1,
        Requerido: "SI",
      },
    ],
    { header: ["Nombre Lista", "Código Insumo", "Cantidad", "Requerido"] },
  );
  hojaInsumos["!cols"] = [{ wch: 40 }, { wch: 14 }, { wch: 10 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, hojaInsumos, "Insumos");

  const referencia = (catalogos.insumos || []).slice(0, 1000).map((i) => ({
    Código: i.codigo ?? "",
    Clave: i.clave || "",
    Descripción: i.descripcion || "",
  }));
  const hojaReferencia =
    referencia.length > 0
      ? XLSX.utils.json_to_sheet(referencia)
      : XLSX.utils.json_to_sheet([{ Código: "(no hay insumos activos)" }]);
  hojaReferencia["!cols"] = [{ wch: 12 }, { wch: 18 }, { wch: 40 }];
  XLSX.utils.book_append_sheet(wb, hojaReferencia, "Catálogo Insumos");

  const insumoPorId = new Map(
    (catalogos.insumos || []).map((i) => [String(i._id), i]),
  );
  const calcularCostoLista = (insumos) =>
    (insumos || []).reduce((acc, i) => {
      const insumo = insumoPorId.get(String(i.insumoId));
      const precio = Number(insumo?.precio || 0);
      const cantidad = Number(i.cantidad || 0);
      return acc + precio * cantidad;
    }, 0);

  const existentes = (catalogos.listas || []).map((l) => {
    const insumos = l.insumos || [];
    const cantInsumos = l.cantidadInsumos ?? insumos.length;
    const cantRequeridos = insumos.filter((i) => i.requerido).length;
    const costoCrudo =
      l.costoTotal !== undefined && l.costoTotal !== null
        ? Number(l.costoTotal)
        : calcularCostoLista(insumos);
    return {
      ID: encodeId(String(l._id)),
      Nombre: l.nombre,
      "# Insumos": cantInsumos,
      "# Requeridos": cantRequeridos,
      "Costo Total": Math.round(costoCrudo * 100) / 100,
      Estado: l.estado === 1 ? "Activo" : "Deshabilitado",
    };
  });
  const hojaExistentes =
    existentes.length > 0
      ? XLSX.utils.json_to_sheet(existentes)
      : XLSX.utils.json_to_sheet([{ Nombre: "(no hay listas registradas)" }]);
  hojaExistentes["!cols"] = [
    { wch: 20 },
    { wch: 40 },
    { wch: 12 },
    { wch: 14 },
    { wch: 14 },
    { wch: 16 },
  ];
  XLSX.utils.book_append_sheet(wb, hojaExistentes, "Existentes");

  return wb;
};

/**
 * Pre-procesa las dos hojas del Excel y construye un mapa
 *   nombreListaLowercase -> [{ insumoId, codigoInsumo, cantidad, requerido }, ...]
 *
 * Lookup por `codigo` (numérico, único por empresa) — NO por clave, porque
 * la clave puede repetirse entre insumos.
 */
const prepararContextoArchivo = (hojas, catalogos) => {
  const hojaInsumos = hojas.find((h) => h.sheetName === "Insumos");
  const hojaListas = hojas[0];

  const nombresListaValidos = new Set();
  for (const fila of hojaListas?.data || []) {
    const nombre = fila["Nombre"] ? String(fila["Nombre"]).trim() : "";
    if (nombre) nombresListaValidos.add(nombre.toLowerCase());
  }

  const insumosPorLista = new Map();
  const erroresInsumos = [];

  if (hojaInsumos) {
    for (const fila of hojaInsumos.data || []) {
      const numFila = (fila["__rowNum__"] ?? 0) + 1;
      const nombreLista = fila["Nombre Lista"]
        ? String(fila["Nombre Lista"]).trim()
        : "";
      const codigoInsumoRaw = fila["Código Insumo"] ?? fila["Codigo Insumo"];
      const cantidadRaw = fila["Cantidad"];
      const requeridoRaw = fila["Requerido"];

      if (
        !nombreLista &&
        (codigoInsumoRaw === undefined || codigoInsumoRaw === "")
      ) {
        continue; // fila vacía
      }

      if (!nombreLista) {
        erroresInsumos.push({
          fila: numFila,
          campo: "Nombre Lista",
          motivo: "está vacío",
          original: fila,
        });
        continue;
      }
      if (codigoInsumoRaw === undefined || codigoInsumoRaw === "") {
        erroresInsumos.push({
          fila: numFila,
          campo: "Código Insumo",
          motivo: "está vacío",
          original: fila,
        });
        continue;
      }

      const codigoInsumoNum = Number(codigoInsumoRaw);
      if (Number.isNaN(codigoInsumoNum)) {
        erroresInsumos.push({
          fila: numFila,
          campo: "Código Insumo",
          motivo: `"${codigoInsumoRaw}" no es un número válido`,
          original: fila,
        });
        continue;
      }

      const llaveLista = nombreLista.toLowerCase();
      if (!nombresListaValidos.has(llaveLista)) {
        erroresInsumos.push({
          fila: numFila,
          campo: "Nombre Lista",
          motivo: `"${nombreLista}" no está en la hoja "Listas"`,
          original: fila,
        });
        continue;
      }

      const insumo = (catalogos.insumos || []).find(
        (i) => Number(i.codigo) === codigoInsumoNum,
      );
      if (!insumo) {
        erroresInsumos.push({
          fila: numFila,
          campo: "Código Insumo",
          motivo: `código "${codigoInsumoNum}" no existe en el catálogo`,
          original: fila,
        });
        continue;
      }

      let cantidad = 1;
      if (cantidadRaw !== undefined && cantidadRaw !== "") {
        const n = Number(cantidadRaw);
        if (Number.isNaN(n) || n <= 0) {
          erroresInsumos.push({
            fila: numFila,
            campo: "Cantidad",
            motivo: "debe ser un número mayor a 0",
            original: fila,
          });
          continue;
        }
        cantidad = n;
      }

      const requerido =
        requeridoRaw !== undefined &&
        ["si", "sí", "yes", "true", "1"].includes(
          String(requeridoRaw).trim().toLowerCase(),
        );

      const arr = insumosPorLista.get(llaveLista) || [];
      arr.push({
        insumoId: insumo._id,
        codigoInsumo: insumo.codigo,
        cantidad,
        requerido,
      });
      insumosPorLista.set(llaveLista, arr);
    }
  }

  if (erroresInsumos.length > 0) {
    console.warn("Errores en hoja Insumos:", erroresInsumos);
  }

  return { insumosPorLista, erroresInsumos };
};

// Serializa un array de insumos como string canónico para comparar igualdad.
const serializarInsumos = (insumos) => {
  return (insumos || [])
    .map((i) => ({
      insumoId: String(i.insumoId),
      cantidad: Number(i.cantidad) > 0 ? Number(i.cantidad) : 1,
      requerido: Boolean(i.requerido),
    }))
    .sort((a, b) => a.insumoId.localeCompare(b.insumoId))
    .map((i) => `${i.insumoId}:${i.cantidad}:${i.requerido ? 1 : 0}`)
    .join("|");
};

const resumenInsumos = (insumos, catalogosInsumos) => {
  const lista = insumos || [];
  const conCodigo = lista.map((i) => {
    const cat = (catalogosInsumos || []).find(
      (c) => String(c._id) === String(i.insumoId || i.insumoId?._id || i._id),
    );
    return {
      codigoInsumo: cat?.codigo ?? "",
      cantidad: i.cantidad,
      requerido: i.requerido,
    };
  });
  const head = conCodigo
    .slice(0, 5)
    .map((i) => `${i.codigoInsumo}${i.requerido ? " *" : ""} x${i.cantidad}`)
    .join(", ");
  const cola = conCodigo.length > 5 ? ` … (+${conCodigo.length - 5})` : "";
  return head + cola;
};

const validarFila = (fila, catalogos, _indice, contexto) => {
  const errores = [];
  const nombre = fila["Nombre"] ? String(fila["Nombre"]).trim() : "";
  const idEncoded = fila["ID"] ? String(fila["ID"]).trim() : "";

  if (!nombre) {
    errores.push({ campo: "Nombre", motivo: "está vacío" });
    return { ok: false, errores };
  }

  const llave = nombre.toLowerCase();

  // Resolver existente: prioridad por ID si viene, si no por nombre.
  let existente = null;
  if (idEncoded) {
    const decoded = decodeId(idEncoded);
    if (!decoded) {
      errores.push({ campo: "ID", motivo: `"${idEncoded}" no es válido` });
    } else {
      existente = (catalogos.listas || []).find(
        (l) => String(l._id) === String(decoded),
      );
      if (!existente) {
        errores.push({
          campo: "ID",
          motivo: `"${idEncoded}" no corresponde a ninguna lista existente`,
        });
      }
    }
  } else {
    existente =
      (catalogos.listas || []).find(
        (l) => (l.nombre || "").trim().toLowerCase() === llave,
      ) || null;
  }

  // Duplicados dentro del mismo archivo (mismo nombre, sin contar a la misma lista).
  if (contexto.clavesYaUsadas.has(llave)) {
    errores.push({
      campo: "Nombre",
      motivo: `nombre repetido en el archivo (también está en la fila ${contexto.clavesYaUsadas.get(llave)})`,
    });
  } else {
    contexto.clavesYaUsadas.set(llave, (fila["__rowNum__"] ?? 0) + 1);
  }

  // Si el nombre coincide con OTRA lista existente (no la que estamos editando), es conflicto.
  if (existente) {
    const conflicto = (catalogos.listas || []).find(
      (l) =>
        (l.nombre || "").trim().toLowerCase() === llave &&
        String(l._id) !== String(existente._id),
    );
    if (conflicto) {
      errores.push({
        campo: "Nombre",
        motivo: `el nombre ya pertenece a otra lista`,
      });
    }
  }

  // Errores de la hoja "Insumos" que pertenecen a esta lista
  const erroresEstaLista = (contexto.erroresInsumos || []).filter(
    (e) =>
      e.original &&
      e.original["Nombre Lista"] &&
      String(e.original["Nombre Lista"]).trim().toLowerCase() === llave,
  );
  for (const er of erroresEstaLista) {
    errores.push({
      campo: `Insumos (fila ${er.fila})`,
      motivo: `${er.campo}: ${er.motivo}`,
    });
  }

  if (errores.length > 0) {
    return { ok: false, errores };
  }

  const insumos = contexto.insumosPorLista?.get(llave) || [];
  const datos = { nombre, insumos };

  if (!existente) {
    return { ok: true, accion: "crear", datos };
  }

  // Comparar contra el existente para detectar cambios.
  const cambios = [];
  const valoresAnteriores = {};

  const nombrePrev = (existente.nombre || "").trim();
  if (nombrePrev !== nombre) {
    cambios.push("nombre");
    valoresAnteriores.nombre = nombrePrev;
  }

  const insumosPrev = existente.insumos || [];
  if (serializarInsumos(insumosPrev) !== serializarInsumos(insumos)) {
    cambios.push("insumos");
    cambios.push("numInsumos");
    valoresAnteriores.insumos = resumenInsumos(insumosPrev, catalogos.insumos);
    valoresAnteriores.numInsumos = insumosPrev.length;
  }

  if (cambios.length === 0) {
    return {
      ok: true,
      accion: "sinCambios",
      idExistente: String(existente._id),
      datos,
      valoresAnteriores: {
        nombre: nombrePrev,
      },
    };
  }

  return {
    ok: true,
    accion: "editar",
    idExistente: String(existente._id),
    datos,
    cambios,
    valoresAnteriores,
  };
};

const instrucciones = [
  <>
    <b>Descarga la plantilla.</b> El archivo trae varias hojas:
    <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
      <li>
        <b>Listas</b> — la hoja principal con el nombre de cada lista a
        importar (columnas <code>ID</code> y <code>Nombre</code>).
      </li>
      <li>
        <b>Insumos</b> — el detalle de qué insumos lleva cada lista. Cada
        renglón es un insumo, asociado a una lista por <code>Nombre Lista</code>.
        Columnas: <code>Nombre Lista</code>, <code>Código Insumo</code>,{" "}
        <code>Cantidad</code>, <code>Requerido</code> (SI / NO).
      </li>
      <li>
        <b>Catálogo Insumos</b> — referencia con los códigos válidos de tus
        insumos activos.
      </li>
      <li>
        <b>Existentes</b> — contiene tus listas actuales con su ID. Cópialas a
        la hoja <b>Listas</b> si quieres editarlas.
      </li>
    </ul>
  </>,
  <>
    Para <b>crear</b> una lista: en la hoja <b>Listas</b> deja la columna{" "}
    <b>ID</b> vacía y pon el nombre. En la hoja <b>Insumos</b> agrega los
    renglones con ese mismo <i>Nombre Lista</i>.
  </>,
  <>
    Para <b>editar</b> una lista: copia su fila desde la hoja{" "}
    <b>Existentes</b> a la hoja <b>Listas</b> (con el ID incluido). En la hoja{" "}
    <b>Insumos</b>, los renglones con ese <i>Nombre Lista</i> reemplazan por
    completo a los insumos anteriores de esa lista.
  </>,
  <>
    El <b>Código Insumo</b> debe ser numérico y existir en la hoja{" "}
    <b>Catálogo Insumos</b> (no uses la clave, usa el código).
  </>,
  <>
    En <b>Requerido</b> pon <code>SI</code> para indicar que el insumo es
    obligatorio, o déjalo vacío / <code>NO</code> para que sea opcional.
  </>,
  <>
    Sube el archivo modificado. Verás un resumen de cuántas listas se van a
    crear, a editar o no tienen cambios. Si hay errores, podrás descargar{" "}
    <code>errores.xlsx</code> para revisarlos.
  </>,
  <>
    En la tabla de vista previa, las listas a <b>Editar</b> muestran el valor
    nuevo y debajo, en gris tachado, el valor anterior.
  </>,
  <>
    Haz clic en <b>Importar</b> para aplicar los cambios.
  </>,
];

const ImportarListas = (props) => (
  <ImportadorExcelGenerico
    {...props}
    titulo="Importar / Actualizar Listas"
    nombreEntidadPlural="listas"
    endpointDatosImportar="listasEscuela/datosParaImportarListas"
    endpointImportar="listasEscuela/importarListas"
    nombrePlantilla="plantillaListasEscuela.xlsx"
    construirPlantilla={construirPlantilla}
    prepararContextoArchivo={prepararContextoArchivo}
    validarFila={validarFila}
    columnasPreview={columnasPreview}
    instrucciones={instrucciones}
    permitirEdicion
    mostrarSwitchSoloCambios
  />
);

export default ImportarListas;
